<script lang="ts">
  import { diffTrees, type FileDiff } from "../lib/diff";
  import type { PasteFile } from "../lib/api";

  interface Props {
    title: string;
    beforeLabel: string;
    before: PasteFile[];
    after: PasteFile[];
    onClose: () => void;
  }

  let { title, beforeLabel, before, after, onClose }: Props = $props();

  const files: FileDiff[] = $derived(diffTrees(before, after));
  const totalAdded = $derived(files.reduce((n, f) => n + f.added, 0));
  const totalRemoved = $derived(files.reduce((n, f) => n + f.removed, 0));
</script>

<div class="backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && onClose()}>
  <div class="dialog" role="dialog" aria-label="diff">
    <div class="dialog-header">
      <span>diff · {title}</span>
      <span class="spacer"></span>
      <span class="stats">
        <span class="add">+{totalAdded}</span>
        <span class="del">−{totalRemoved}</span>
      </span>
      <button onclick={onClose} title="close">×</button>
    </div>

    <div class="dialog-body">
      <div class="legend muted">{beforeLabel} → текущее состояние</div>

      {#if files.length === 0}
        <div class="empty muted">изменений нет</div>
      {:else}
        {#each files as file (file.path)}
          <section class="file">
            <header>
              <span class="path">{file.path}</span>
              <span class="badge {file.status}">{file.status}</span>
              <span class="spacer"></span>
              {#if file.added}<span class="add">+{file.added}</span>{/if}
              {#if file.removed}<span class="del">−{file.removed}</span>{/if}
            </header>
            <div class="rows">
              {#each file.rows as row}
                {#if row.type === "gap"}
                  <div class="row gap">⋯ {row.count} строк</div>
                {:else}
                  <div class="row {row.type}">
                    <span class="no">{row.oldNo ?? ""}</span>
                    <span class="no">{row.newNo ?? ""}</span>
                    <span class="sign">{row.type === "add" ? "+" : row.type === "del" ? "−" : " "}</span>
                    <span class="text">{row.text}</span>
                  </div>
                {/if}
              {/each}
            </div>
          </section>
        {/each}
      {/if}
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 120;
  }
  .dialog {
    width: min(1000px, calc(100vw - 40px));
    max-height: calc(100vh - 60px);
    display: flex;
    flex-direction: column;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 6px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
  .dialog-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-bottom: 1px solid var(--border);
  }
  .dialog-header button { padding: 1px 6px; }
  .spacer { flex: 1; }
  .stats { display: flex; gap: 8px; }
  .add { color: #4ec9b0; }
  .del { color: #f48771; }
  .dialog-body { overflow: auto; padding: 10px 12px; }
  .legend { margin-bottom: 10px; }
  .empty { text-align: center; padding: 20px; }

  .file { margin-bottom: 14px; border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
  .file header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }
  .path { color: var(--fg); }
  .badge {
    padding: 0 5px;
    border-radius: 3px;
    border: 1px solid var(--border);
    color: var(--fg-dim);
  }
  .badge.added { color: #4ec9b0; border-color: #2e5b52; }
  .badge.removed { color: #f48771; border-color: #5b342e; }

  .rows { font-family: var(--mono); font-size: var(--size); }
  .row {
    display: flex;
    gap: 8px;
    padding: 0 8px;
    white-space: pre;
    line-height: 1.5;
  }
  .row .no {
    width: 3.5ch;
    text-align: right;
    color: var(--fg-dim);
    flex-shrink: 0;
    user-select: none;
  }
  .row .sign { width: 1ch; flex-shrink: 0; }
  .row .text { white-space: pre-wrap; word-break: break-word; }
  .row.add { background: rgba(78, 201, 176, 0.1); }
  .row.add .sign { color: #4ec9b0; }
  .row.del { background: rgba(244, 135, 113, 0.1); }
  .row.del .sign { color: #f48771; }
  .row.gap {
    color: var(--fg-dim);
    justify-content: center;
    background: var(--bg);
  }
</style>